import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Box_Shadow() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📦 Advanced CSS: Box Shadow</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>box-shadow</code> property allows you to create depth, glow,
        and UI effects. Beyond simple shadows, you can use{" "}
        <b>multiple shadows, inset shadows, glowing effects, and neumorphism</b>{" "}
        to build modern interfaces.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>box-shadow</code> syntax →{" "}
            <code>offset-x offset-y blur spread color</code>
          </li>
          <li>
            <b>Inset shadows</b> create an inner shadow inside the box.
          </li>
          <li>
            You can apply <b>multiple shadows</b> by separating them with
            commas.
          </li>
          <li>
            Use shadows to create <b>glowing buttons</b> and <b>3D cards</b>.
          </li>
          <li>Neumorphism = soft UI using light & dark shadows together.</li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Syntax</h2>
        <CodeCopyBlock
          code={`/* Basic syntax */
box-shadow: offset-x offset-y blur-radius spread-radius color;

/* Example */
box-shadow: 4px 6px 12px rgba(0, 0, 0, 0.3);

/* Inset shadow */
box-shadow: inset 2px 2px 6px rgba(0,0,0,0.4);`}
        />
      </div>

      {/* Basic Example */}
      <div className="tutorial-section">
        <h2 className="section-title">📥 Basic Shadow</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#fff",
            borderRadius: "10px",
            boxShadow: "4px 6px 12px rgba(0,0,0,0.2)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          Basic Shadow
        </div>
        <CodeCopyBlock
          code={`div { box-shadow: 4px 6px 12px rgba(0,0,0,0.2); }`}
        />
      </div>

      {/* Inset Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">🔲 Inset Shadow</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#e5e7eb",
            borderRadius: "10px",
            boxShadow: "inset 4px 4px 8px rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          Inset Shadow
        </div>
        <CodeCopyBlock
          code={`div { box-shadow: inset 4px 4px 8px rgba(0,0,0,0.3); }`}
        />
      </div>

      {/* Multiple Shadows */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Multiple Shadows</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#fff",
            borderRadius: "10px",
            boxShadow:
              "2px 2px 6px rgba(0,0,0,0.3), -2px -2px 6px rgba(0,0,0,0.1)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          Multiple Shadows
        </div>
        <CodeCopyBlock
          code={`div {
  box-shadow:
    2px 2px 6px rgba(0,0,0,0.3),
    -2px -2px 6px rgba(0,0,0,0.1);
}`}
        />
      </div>

      {/* Glowing Effect */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Glowing Effect</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#3b82f6",
            borderRadius: "10px",
            color: "#fff",
            fontWeight: "bold",
            textAlign: "center",
            lineHeight: "100px",
            boxShadow: "0 0 10px #3b82f6, 0 0 20px #60a5fa, 0 0 40px #93c5fd",
            marginBottom: "1rem",
          }}
        >
          Glow Box
        </div>
        <CodeCopyBlock
          code={`div {
  background: #3b82f6;
  color: #fff;
  box-shadow:
    0 0 10px #3b82f6,
    0 0 20px #60a5fa,
    0 0 40px #93c5fd;
}`}
        />
      </div>

      {/* Neumorphism Example */}
      <div className="tutorial-section">
        <h2 className="section-title">💎 Neumorphism Card</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#e0e0e0",
            borderRadius: "20px",
            boxShadow:
              "8px 8px 15px rgba(0,0,0,0.2), -8px -8px 15px rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            marginBottom: "1rem",
          }}
        >
          Soft UI
        </div>
        <CodeCopyBlock
          code={`div {
  background: #e0e0e0;
  border-radius: 20px;
  box-shadow:
    8px 8px 15px rgba(0,0,0,0.2),
    -8px -8px 15px rgba(255,255,255,0.7);
}`}
        />
      </div>

      {/* 3D Shadow Effect */}
      <div className="tutorial-section">
        <h2 className="section-title">🕹️ 3D Shadow Effect</h2>
        <div
          style={{
            width: "200px",
            height: "100px",
            background: "#fef3c7",
            borderRadius: "10px",
            boxShadow: "8px 8px 0px #f59e0b",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            marginBottom: "1rem",
          }}
        >
          3D Box
        </div>
        <CodeCopyBlock
          code={`div {
  background: #fef3c7;
  box-shadow: 8px 8px 0px #f59e0b;
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>box-shadow</code> with <b>hover effects</b> for
            interactive UI.
          </li>
          <li>
            Create <b>neon glow animations</b> with <code>@keyframes</code>.
          </li>
          <li>
            Use shadows with <code>clip-path</code> or{" "}
            <code>border-radius</code> for unique styles.
          </li>
          <li>
            Experiment with <b>soft UI (neumorphism)</b> for dashboards and
            cards.
          </li>
          <li>
            Explore <code>filter: drop-shadow()</code> for images and SVG
            elements.
          </li>
        </ul>
      </div>
    </div>
  );
}
